<script setup lang="ts">
import { Camera, MessageCircle, Pencil } from '@lucide/vue'
import { memberEmail, memberMailDomain } from '@zerohuman/shared'
import {
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Input,
    Label,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    TagsInput,
    TagsInputItem,
    TagsInputItemDelete,
    TagsInputItemText,
    Textarea,
    ZhPageTitle,
    ZhPanel,
    ZhSectionHeading,
    ZhSystemLabel,
} from '@zerohuman/ui'
import { computed, onMounted, ref } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import {
    api,
    type MailboxOwnership,
    type MailboxOwnershipChange,
    type Member,
    type MemberToolGap,
    type Role,
    type Team,
} from '@/api'
import AgentConnectionsPanel from '@/components/AgentConnectionsPanel.vue'
import HolderToolGaps from '@/components/HolderToolGaps.vue'
import MailAliasesPanel from '@/components/MailAliasesPanel.vue'
import MailboxPanel from '@/components/MailboxPanel.vue'
import MemberAvatar from '@/components/MemberAvatar.vue'
import ToolBindings from '@/components/ToolBindings.vue'
import { useUiMode } from '@/composables/useUiMode'
import { avatarDataUrl } from '@/lib/avatar'
import type { HolderToolGapsState } from '@/lib/holderToolGaps'
import { memberMailboxSource } from '@/lib/mailboxSources'
import { memberLabel } from '@/lib/memberLabel'
import { pickedId } from '@/lib/memberPickers'
import { toolLabel } from '@/lib/presentation'
import {
    type HandedTool,
    noticeSentence,
    stillToConnect,
    takeTeammateNotice,
    teammateHandoff,
} from '@/lib/teammateHandoff'
import { connectToolLocation } from '@/lib/teamSetup'

/**
 * Every chip, control, surface and callout here is the kit's (#1223): the markers are kit chips, the persona editor
 * the kit `Textarea`, the add-to-team and assign-role pickers the kit `Select`, the Inbox list a kit surface, and the
 * mailbox ownership control a kit Select. The screen states no colour, radius, border or fill of its own.
 * A Select's `aria-label` lands on the field it mirrors its choice on; a visually hidden span names each trigger.
 */
const route = useRoute()
const router = useRouter()
const memberId = String(route.params.id)
const member = ref<Member | null>(null)
const roles = ref<Role[]>([])
const allTeams = ref<Team[]>([])
const allMembers = ref<Member[]>([])
const isEnterpriseOwner = ref(false)
/** The signed-in enterprise's slug, so a member with no address can be offered `{name}@{slug}.zerohuman.com`. */
const enterpriseSlug = ref<string | null>(null)
const pickTeam = ref('')
const loadError = ref<string | null>(null)
const error = ref<string | null>(null)
const busy = ref(false)
const pick = ref('')
const editingName = ref(false)
const nameInput = ref('')
const editingEmail = ref(false)
const emailInput = ref('')
const editingPersona = ref(false)
const personaInput = ref('')
const editingDescription = ref(false)
const descriptionInput = ref('')
const editingConcurrency = ref(false)
const concurrencyInput = ref<string | number>('')
/**
 * The most tasks at once this member may be set to: the plan's figure for one member, held to the company's concurrent
 * runs. The setting is offered only where that is more than 1; on every other plan a member runs one task at a time.
 */
const concurrencyMax = computed(() => member.value?.maxConcurrencyCap ?? 1)
const offersConcurrency = computed(() => concurrencyMax.value > 1)
/** Set while asking whether a rename should also change the email. */
const emailPrompt = ref<{ name: string; email: string } | null>(null)
const avatarInput = ref<HTMLInputElement | null>(null)
/**
 * The member's mailbox, through the shared panel (`memberMailboxSource`): read by anybody who reads the member, with the
 * one-click buttons of its tasks, and worked like a mail client by the enterprise owner, whose replies leave as the
 * member. Rebuilt once the page knows whether the viewer owns the enterprise.
 */
const mailboxSource = computed(() => memberMailboxSource(memberId, isEnterpriseOwner.value))
const mailboxOwnership = ref<MailboxOwnership | null>(null)
const pendingMailboxOwnerId = ref<string | null>(null)
const transferExistingMail = ref(true)
const mailboxNotice = ref<string | null>(null)

const pendingMailboxOwner = computed(() =>
    allMembers.value.find(candidate => candidate.id === pendingMailboxOwnerId.value),
)
const restoringMailboxToSelf = computed(() => pendingMailboxOwner.value?.id === member.value?.id)
/**
 * The mailbox owner as the page labels every member (#1740): the mailbox-owner read carries only the stored name,
 * which is blank for an unnamed teammate, so the owner is labelled from the member list the owner picker uses.
 */
function ownerLabel(owner: { id: string; name: string }): string {
    const known = allMembers.value.find(candidate => candidate.id === owner.id)
    return known ? memberLabel(known) : owner.name
}

const availableRoles = computed(() => {
    const held = new Set(member.value?.roles.map(role => role.id))
    return roles.value.filter(role => !held.has(role.id))
})

const memberTeams = computed(() => member.value?.teams ?? [])
const availableTeams = computed(() => {
    const held = new Set(memberTeams.value.map(team => team.id))
    return allTeams.value.filter(team => !held.has(team.id))
})
function teamLabel(teamId: unknown): string {
    const row = memberTeams.value.find(team => team.id === teamId)
    if (!row) {
        return String(teamId)
    }
    return row.lead ? `${row.name} (lead)` : row.name
}

/**
 * Picking a team joins it, the way picking a role assigns it — no second step. Roles are held
 * independently of teams, so the role picker that used to sit here only duplicated the Roles section
 * one heading down.
 *
 * The choice arrives from the kit Select's model update; the picker is never left holding it.
 */
async function addToTeam(choice: unknown): Promise<void> {
    const teamId = pickedId(choice)
    pickTeam.value = ''
    if (!teamId) {
        return
    }
    await act('Could not add to team.', () => api.addTeamMember(teamId, memberId, null))
}

async function removeFromTeam(teamId: unknown): Promise<void> {
    await act('Could not remove from team.', () => api.removeTeamMember(String(teamId), memberId))
}

const { mode } = useUiMode()
/**
 * #1758: the tools a teammate just made with Add a teammate still has no connection for, each with Connect. Add a
 * teammate keeps what their tasks need for this tab; a tool connected since (for the company, one of their teams or
 * them) drops off. A member nobody handed on shows nothing, and the page reads no connections for it.
 */
const toolsToConnect = ref<HandedTool[]>([])

async function loadToolsToConnect(): Promise<void> {
    const handed = teammateHandoff(memberId)?.tools ?? []
    if (!handed.length) return
    const teamIds = memberTeams.value.map(team => team.id)
    try {
        toolsToConnect.value = stillToConnect(handed, await api.toolBindings(), memberId, teamIds)
    } catch {
        // Connections that cannot be read leave every handed tool listed: Connect is still the way on.
        toolsToConnect.value = handed
    }
}

async function connectTool(tool: string): Promise<void> {
    await router.push(connectToolLocation(tool))
}

/**
 * #637: the tools this member's roles' tasks need that no binding of theirs reaches, read from the API (computed there
 * from their roles, those roles' tasks and the binding cascade), one row per vendor with Connect. While the read is out
 * the page says it is checking; a failed read says so with Try again, and leaves the rest of the page as it is.
 */
const toolGaps = ref<MemberToolGap[]>([])
const toolGapsState = ref<HolderToolGapsState | undefined>('checking')

async function loadToolGaps(): Promise<void> {
    toolGapsState.value = 'checking'
    try {
        toolGaps.value = await api.memberToolGaps(memberId)
        toolGapsState.value = undefined
    } catch {
        toolGaps.value = []
        toolGapsState.value = 'failed'
    }
}

/**
 * #2128: when a step after Add a teammate made this teammate did not happen, the page says once that they were added
 * and what was not done, with a link to each next step still waiting: connecting their own AI account, a described
 * job's first task, and Spend for a cap that was not set. Taken as the page opens, so opening it again shows nothing.
 */
const addedNotice = takeTeammateNotice(memberId)
const addedSentence = addedNotice ? noticeSentence(addedNotice) : ''
const addedNextSteps = addedNotice
    ? [
          ...(addedNotice.connectOwn
              ? [
                    {
                        label: 'Connect their own AI account',
                        to: { path: '/agents', query: { connect: 'member', memberId } },
                    },
                ]
              : []),
          ...(addedNotice.firstTask
              ? [{ label: 'Create their first task', to: { path: '/tasks/new', query: { ...addedNotice.firstTask } } }]
              : []),
          ...(addedNotice.capMissed ? [{ label: 'Set their daily cap on Spend', to: { path: '/spend' } }] : []),
      ]
    : []

onMounted(async () => {
    await load()
    await Promise.all([loadToolsToConnect(), loadToolGaps()])
})

async function load(): Promise<void> {
    try {
        const [row, allRoles, teams, people, me, ownership] = await Promise.all([
            api.member(memberId),
            api.roles(),
            api.teams().catch(() => [] as Team[]),
            api.members().catch(() => [] as Member[]),
            api.me().catch(() => null),
            api.mailboxOwner(memberId).catch(() => null),
        ])
        member.value = row
        roles.value = allRoles
        allTeams.value = teams
        allMembers.value = people
        isEnterpriseOwner.value = me?.isOwner === true
        enterpriseSlug.value = me?.enterprise?.slug ?? null
        mailboxOwnership.value = ownership
        loadError.value = null
    } catch (caught) {
        loadError.value =
            caught instanceof Error && caught.message === 'member_not_found'
                ? 'Member not found.'
                : 'Could not load member.'
    }
}

function requestMailboxOwner(choice: unknown): void {
    const ownerId = pickedId(choice)
    if (!ownerId || ownerId === mailboxOwnership.value?.owner.id) {
        return
    }
    pendingMailboxOwnerId.value = ownerId
    transferExistingMail.value = (mailboxOwnership.value?.existingMessages ?? 0) > 0
}

async function confirmMailboxOwner(): Promise<void> {
    const ownerId = pendingMailboxOwnerId.value
    if (!ownerId) {
        return
    }
    busy.value = true
    error.value = null
    mailboxNotice.value = null
    try {
        const changed: MailboxOwnershipChange = await api.setMailboxOwner(memberId, {
            ownerId,
            transferExisting: transferExistingMail.value,
        })
        const moved = changed.transferredMessages
        const present = changed.alreadyPresentMessages
        mailboxNotice.value = `${ownerLabel(changed.owner)} now owns ${changed.address}.${
            moved > 0 ? ` Moved ${moved} existing ${moved === 1 ? 'message' : 'messages'}.` : ''
        }${present > 0 ? ` ${present} ${present === 1 ? 'message already had a copy' : 'messages already had copies'} and stayed in place.` : ''}`
        pendingMailboxOwnerId.value = null
        await load()
    } catch (caught) {
        error.value =
            caught instanceof Error
                ? `Could not change mailbox owner. (${caught.message}).`
                : 'Could not change mailbox owner.'
    } finally {
        busy.value = false
    }
}

/** Run a member change; the page reloads the member afterwards either way. */
async function act(failure: string, change: () => Promise<unknown>): Promise<boolean> {
    busy.value = true
    error.value = null
    try {
        await change()
        return true
    } catch (caught) {
        error.value = caught instanceof Error ? `${failure} (${caught.message}).` : `${failure}.`
        return false
    } finally {
        await load()
        busy.value = false
    }
}

function roleName(roleId: unknown): string {
    return roles.value.find(role => role.id === roleId)?.name ?? String(roleId)
}

/** A role picked through the kit Select is assigned at once, and the picker goes back to empty. */
async function assign(choice: unknown): Promise<void> {
    const roleId = pickedId(choice)
    pick.value = ''
    if (member.value && roleId) {
        await act('Could not assign role', () => api.assignRole(memberId, roleId))
    }
}

async function removeRole(roleId: unknown): Promise<void> {
    await act('Could not remove role', () => api.removeRole(memberId, String(roleId)))
}

async function startChat(): Promise<void> {
    await act('Could not start chat', async () => {
        const session = await api.createChat(memberId)
        await router.push(`/chat/${session.id}`)
    })
}

function startRename(): void {
    nameInput.value = member.value?.name ?? ''
    editingName.value = true
}

function submitName(): void {
    const current = member.value
    const name = nameInput.value.trim().replace(/\s+/g, ' ')
    if (!current || !name) {
        return
    }
    if (name === current.name) {
        editingName.value = false
        return
    }
    const email = renameAddress(name, current.email, enterpriseSlug.value)
    if (email && email !== current.email) {
        emailPrompt.value = { name, email }
        return
    }
    void rename({ name })
}

/**
 * The address a rename offers alongside the new name. A member who already has an address keeps that domain.
 * One stored with no address has nothing after the `@` to keep: taking that empty string as the domain produced
 * `name@`, which the API refuses as `invalid_member_email`. They are offered the enterprise's own mail domain.
 */
function renameAddress(name: string, currentEmail: string, slug: string | null): string | null {
    const at = currentEmail.lastIndexOf('@')
    const currentDomain = at >= 0 ? currentEmail.slice(at + 1) : ''
    let domain = ''
    if (domainCanHost(currentDomain)) {
        domain = currentDomain
    } else if (slug) {
        domain = memberMailDomain(slug)
    }
    if (!domain) {
        return null
    }
    const email = memberEmail(name, domain)
    return email.startsWith('@') ? null : email
}

/** Whether `domain` is a host the member-address check will keep: a dotted name, in either case. */
function domainCanHost(domain: string): boolean {
    return /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(domain)
}

async function rename(body: { name: string; email?: string }): Promise<void> {
    emailPrompt.value = null
    const ok = await act('Could not rename member', () => api.patchMember(memberId, body))
    if (ok) {
        editingName.value = false
    } else if (error.value?.includes('member_email_taken')) {
        error.value = `Could not rename member: ${body.email} is already another member’s email.`
    }
}

function startEmailEdit(): void {
    emailInput.value = member.value?.email ?? ''
    editingEmail.value = true
}

async function submitEmail(): Promise<void> {
    const current = member.value
    const email = emailInput.value.trim().toLowerCase()
    if (!current || !email) {
        return
    }
    if (email === current.email) {
        editingEmail.value = false
        return
    }
    const ok = await act('Could not change email', () => api.patchMember(memberId, { email }))
    if (ok) {
        editingEmail.value = false
    } else if (error.value?.includes('member_email_taken')) {
        error.value = `Could not change email: ${email} is already another member’s email.`
    }
}

function startPersonaEdit(): void {
    personaInput.value = member.value?.persona ?? ''
    editingPersona.value = true
}

/** An emptied box clears the persona, so the field is sent as null rather than left untouched. */
async function submitPersona(): Promise<void> {
    const persona = personaInput.value.trim()
    const ok = await act('Could not save persona', () => api.patchMember(memberId, { persona: persona || null }))
    if (ok) {
        editingPersona.value = false
    }
}

function startDescriptionEdit(): void {
    descriptionInput.value = member.value?.description ?? ''
    editingDescription.value = true
}

/**
 * The public description is its own field: saving sends only it, never the persona. An emptied box clears it, so
 * it is sent as null and the public card loses its line once the public data refreshes.
 */
async function submitDescription(): Promise<void> {
    const description = descriptionInput.value.trim()
    const ok = await act('Could not save public description', () =>
        api.patchMember(memberId, { description: description || null }),
    )
    if (ok) {
        editingDescription.value = false
    }
}

function startConcurrencyEdit(): void {
    concurrencyInput.value = String(member.value?.concurrencyCap ?? 1)
    editingConcurrency.value = true
}

/** A whole number from 1 to the plan's figure; the API refuses anything else with its own words. */
async function submitConcurrency(): Promise<void> {
    const concurrencyCap = Number(concurrencyInput.value)
    if (!Number.isInteger(concurrencyCap) || concurrencyCap < 1) {
        error.value = 'Tasks at once is a whole number, 1 or more.'
        return
    }
    const ok = await act('Could not save tasks at once', () => api.patchMember(memberId, { concurrencyCap }))
    if (ok) {
        editingConcurrency.value = false
    }
}

async function onAvatarPicked(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    input.value = ''
    if (file) {
        await act('Could not update photo', async () => api.setMemberAvatar(memberId, await avatarDataUrl(file)))
    }
}
</script>

<template>
	<section class="w-full space-y-6 p-8">
		<p v-if="loadError" class="text-destructive text-sm">{{ loadError }}</p>
		<template v-else-if="member">
			<header class="flex flex-wrap items-center gap-5">
				<div class="relative shrink-0">
					<MemberAvatar :member="member" class="size-24" fallback-class="text-3xl" />
					<Button
						type="button"
						size="icon-sm"
						variant="outline"
						class="absolute right-0 bottom-0"
						aria-label="Change photo"
						:disabled="busy"
						@click="avatarInput?.click()"
					>
						<Camera />
					</Button>
					<input
						ref="avatarInput"
						type="file"
						accept="image/png,image/jpeg,image/webp,image/gif"
						class="hidden"
						@change="onAvatarPicked"
					/>
				</div>
				<div class="min-w-0 flex-1 space-y-1">
					<form v-if="editingName" class="flex flex-wrap items-center gap-2" @submit.prevent="submitName">
						<Input
							v-model="nameInput"
							aria-label="Full name"
							maxlength="80"
							required
							class="max-w-sm text-lg md:text-lg"
							autofocus
						/>
						<Button type="submit" size="sm" :disabled="busy || !nameInput.trim()">Save</Button>
						<Button type="button" size="sm" variant="outline" :disabled="busy" @click="editingName = false">
							Cancel
						</Button>
					</form>
					<div v-else class="flex items-center gap-1">
						<ZhPageTitle kicker="Team member">{{ memberLabel(member) }}</ZhPageTitle>
						<Button type="button" size="icon-sm" variant="ghost" aria-label="Edit name" @click="startRename">
							<Pencil />
						</Button>
					</div>
					<form v-if="editingEmail" class="flex flex-wrap items-center gap-2" @submit.prevent="submitEmail">
						<Input
							v-model="emailInput"
							type="email"
							aria-label="Email"
							maxlength="320"
							required
							class="max-w-sm"
							autofocus
						/>
						<Button type="submit" size="sm" :disabled="busy || !emailInput.trim()">Save</Button>
						<Button type="button" size="sm" variant="outline" :disabled="busy" @click="editingEmail = false">
							Cancel
						</Button>
					</form>
					<p v-else class="text-muted-foreground flex flex-wrap items-center gap-1 text-sm">
						{{ member.email }}
						<Button type="button" size="icon-sm" variant="ghost" aria-label="Edit email" @click="startEmailEdit">
							<Pencil />
						</Button>
						· this agent’s own tools and model keys bind here
					</p>
					<p v-if="member.leadOf.length" class="flex flex-wrap gap-2">
						<ZhSystemLabel v-for="teamName in member.leadOf" :key="teamName" as="span">
							Lead · {{ teamName }}
						</ZhSystemLabel>
					</p>
					<div class="flex flex-wrap gap-2 pt-1">
						<Button type="button" size="xs" :disabled="busy" @click="startChat">
							<MessageCircle class="size-3.5" />
							Chat
						</Button>
					</div>
				</div>
			</header>
			<p v-if="error" class="text-destructive text-sm">{{ error }}</p>

			<ZhPanel v-if="addedNotice" class="space-y-3" role="status" data-testid="member-added-notice">
				<p class="text-sm">{{ addedSentence }}</p>
				<div v-if="addedNextSteps.length" class="flex flex-wrap gap-2">
					<Button v-for="step in addedNextSteps" :key="step.label" as-child size="sm" variant="outline">
						<RouterLink :to="step.to">{{ step.label }}</RouterLink>
					</Button>
				</div>
			</ZhPanel>

			<ZhPanel v-if="toolsToConnect.length" class="space-y-3" data-testid="member-tools-to-connect">
				<ZhSectionHeading>Tools to connect</ZhSectionHeading>
				<p class="text-muted-foreground text-sm">
					{{ memberLabel(member) }}’s tasks use these, and your company has not connected them yet.
				</p>
				<div
					v-for="need in toolsToConnect"
					:key="need.tool"
					class="flex flex-wrap items-center justify-between gap-2"
					:data-testid="`member-tool-${need.tool}`"
				>
					<p class="text-sm">
						<span class="font-medium">{{ toolLabel(need.tool, mode) }}</span>
						<span v-if="need.tasks.length" class="text-muted-foreground">
							· needed by {{ need.tasks.join(', ') }}
						</span>
					</p>
					<Button type="button" size="sm" variant="outline" @click="connectTool(need.tool)">Connect</Button>
				</div>
			</ZhPanel>

			<HolderToolGaps
				:entries="[{ memberId: member.id, memberName: memberLabel(member), gaps: toolGaps, state: toolGapsState }]"
				section-testid="member-tool-gaps"
				row-testid="member-tool-gap"
				@retry="loadToolGaps"
			/>

			<section class="space-y-3" data-testid="member-persona">
				<div class="flex flex-wrap items-center gap-1">
					<ZhSectionHeading>Persona</ZhSectionHeading>
					<Button
						v-if="!editingPersona"
						type="button"
						size="icon-sm"
						variant="ghost"
						aria-label="Edit persona"
						:disabled="busy"
						@click="startPersonaEdit"
					>
						<Pencil />
					</Button>
				</div>
				<form v-if="editingPersona" class="space-y-2" @submit.prevent="submitPersona">
					<Textarea v-model="personaInput" aria-label="Persona" rows="3" maxlength="500" />
					<div class="flex gap-2">
						<Button type="submit" size="sm" :disabled="busy">Save</Button>
						<Button type="button" size="sm" variant="outline" :disabled="busy" @click="editingPersona = false">
							Cancel
						</Button>
					</div>
				</form>
				<p v-else-if="member.persona" class="text-sm whitespace-pre-wrap">{{ member.persona }}</p>
				<p v-else class="text-muted-foreground text-sm">No persona. It tells this member's agent who it is.</p>
			</section>

			<section class="space-y-3" data-testid="member-description">
				<div class="flex flex-wrap items-center gap-1">
					<ZhSectionHeading>Public description</ZhSectionHeading>
					<Button
						v-if="!editingDescription"
						type="button"
						size="icon-sm"
						variant="ghost"
						aria-label="Edit public description"
						:disabled="busy"
						@click="startDescriptionEdit"
					>
						<Pencil />
					</Button>
				</div>
				<p class="text-muted-foreground text-xs">
					Published on the public site’s About page. Separate from the persona, which is never published.
				</p>
				<form v-if="editingDescription" class="space-y-2" @submit.prevent="submitDescription">
					<Textarea v-model="descriptionInput" aria-label="Public description" rows="3" maxlength="280" />
					<div class="flex gap-2">
						<Button type="submit" size="sm" :disabled="busy">Save</Button>
						<Button
							type="button"
							size="sm"
							variant="outline"
							:disabled="busy"
							@click="editingDescription = false"
						>
							Cancel
						</Button>
					</div>
				</form>
				<p v-else-if="member.description" class="text-sm whitespace-pre-wrap">{{ member.description }}</p>
				<p v-else class="text-muted-foreground text-sm">No public description.</p>
			</section>

			<section v-if="offersConcurrency" class="space-y-3" data-testid="member-concurrency">
				<div class="flex flex-wrap items-center gap-1">
					<ZhSectionHeading>Tasks at once</ZhSectionHeading>
					<Button
						v-if="!editingConcurrency"
						type="button"
						size="icon-sm"
						variant="ghost"
						aria-label="Edit tasks at once"
						:disabled="busy"
						@click="startConcurrencyEdit"
					>
						<Pencil />
					</Button>
				</div>
				<p class="text-muted-foreground text-xs">
					How many tasks {{ memberLabel(member) }} may work on at the same time, up to {{ concurrencyMax }}.
				</p>
				<form
					v-if="editingConcurrency"
					class="flex flex-wrap items-center gap-2"
					@submit.prevent="submitConcurrency"
				>
					<Input
						v-model="concurrencyInput"
						type="number"
						inputmode="numeric"
						min="1"
						:max="concurrencyMax"
						step="1"
						required
						aria-label="Tasks at once"
						class="w-24"
					/>
					<Button type="submit" size="sm" :disabled="busy">Save</Button>
					<Button
						type="button"
						size="sm"
						variant="outline"
						:disabled="busy"
						@click="editingConcurrency = false"
					>
						Cancel
					</Button>
				</form>
				<p v-else class="text-sm">{{ member.concurrencyCap ?? 1 }}</p>
			</section>

			<section class="space-y-3" data-testid="member-teams">
				<ZhSectionHeading>Teams</ZhSectionHeading>
				<TagsInput
					:model-value="memberTeams.map(team => team.id)"
					:display-value="teamLabel"
					:disabled="busy"
					class="min-h-9"
					@remove-tag="removeFromTeam"
				>
					<TagsInputItem v-for="row in memberTeams" :key="row.id" :value="row.id">
						<TagsInputItemText />
						<TagsInputItemDelete tabindex="0" :aria-label="`Remove from ${row.name}`" />
					</TagsInputItem>
					<template v-if="availableTeams.length">
						<span id="member-add-team" class="sr-only">Add to team</span>
						<Select
							:model-value="pickTeam"
							:disabled="busy"
							aria-label="Add to team"
							@update:model-value="addToTeam"
						>
							<SelectTrigger size="sm" aria-labelledby="member-add-team">
								<SelectValue placeholder="Add to team…" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem v-for="row in availableTeams" :key="row.id" :value="row.id">{{ row.name }}</SelectItem>
							</SelectContent>
						</Select>
					</template>
					<span v-else-if="!memberTeams.length" class="text-muted-foreground px-1">Not on a team.</span>
				</TagsInput>
			</section>

			<section class="space-y-3">
				<ZhSectionHeading>Roles</ZhSectionHeading>
				<TagsInput
					:model-value="member.roles.map(role => role.id)"
					:display-value="roleName"
					:disabled="busy"
					class="min-h-9"
					@remove-tag="removeRole"
				>
					<TagsInputItem v-for="role in member.roles" :key="role.id" :value="role.id">
						<TagsInputItemText />
						<TagsInputItemDelete tabindex="0" :aria-label="`Remove ${role.name}`" />
					</TagsInputItem>
					<template v-if="availableRoles.length">
						<span id="member-assign-role" class="sr-only">Assign role</span>
						<Select :model-value="pick" :disabled="busy" aria-label="Assign role" @update:model-value="assign">
							<SelectTrigger size="sm" aria-labelledby="member-assign-role">
								<SelectValue placeholder="Assign role…" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem v-for="role in availableRoles" :key="role.id" :value="role.id">
									{{ role.name }}
								</SelectItem>
							</SelectContent>
						</Select>
					</template>
					<span v-else-if="!member.roles.length" class="text-muted-foreground px-1">No roles to assign.</span>
				</TagsInput>
			</section>

			<section class="space-y-3">
				<ZhSectionHeading>Active run</ZhSectionHeading>
				<p v-if="member.activeRun" class="flex flex-wrap items-center gap-2 text-sm">
					<RouterLink :to="`/runs/${member.activeRun.id}`" class="font-medium underline-offset-2 hover:underline">
						{{ member.activeRun.taskName }}
					</RouterLink>
					<ZhSystemLabel as="span">{{ member.activeRun.status }}</ZhSystemLabel>
				</p>
				<p v-else class="text-muted-foreground text-sm">No active run.</p>
			</section>

			<section class="space-y-3">
				<ZhSectionHeading>Tasks</ZhSectionHeading>
				<p class="text-muted-foreground text-sm">The tasks of the roles this member holds, on the teams they sit on. Their runs can go to this member.</p>
				<ul v-if="member.ownedTasks?.length" data-testid="member-tasks" class="space-y-1 text-sm">
					<li v-for="task in member.ownedTasks" :key="String(task.id)">
						<RouterLink :to="`/tasks/${task.id}`" class="underline-offset-2 hover:underline">
							{{ task.name }}
						</RouterLink>
					</li>
				</ul>
				<p v-else class="text-muted-foreground text-sm">No tasks. No team this member sits on has a task for a role they hold.</p>
			</section>

			<section class="space-y-3">
				<ZhSectionHeading>Agents</ZhSectionHeading>
				<AgentConnectionsPanel layer="member" :layer-id="member.id" />
			</section>
			<section class="space-y-3">
				<ZhSectionHeading>Tools</ZhSectionHeading>
				<ToolBindings lock-layer="member" :lock-layer-id="member.id" />
			</section>

			<section id="mail" class="space-y-3">
				<ZhSectionHeading>Mail</ZhSectionHeading>
                <MailAliasesPanel :member-id="member.id" :editable="isEnterpriseOwner" />
				<ZhPanel v-if="mailboxOwnership" data-testid="mailbox-owner" class="space-y-3">
					<div class="space-y-1">
						<p class="font-medium">Mailbox owner</p>
						<p class="text-muted-foreground text-sm">
							{{ ownerLabel(mailboxOwnership.owner) }} receives new mail to {{ mailboxOwnership.address }} and may send,
							reply and chase from that address.
						</p>
					</div>
					<template v-if="isEnterpriseOwner">
						<span id="mailbox-owner-label" class="sr-only">Mailbox owner</span>
						<Select
							:model-value="mailboxOwnership.owner.id"
							:disabled="busy"
							aria-label="Mailbox owner"
							data-testid="mailbox-owner-select"
							@update:model-value="requestMailboxOwner"
						>
							<SelectTrigger class="w-72" aria-labelledby="mailbox-owner-label">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem v-for="candidate in allMembers" :key="candidate.id" :value="candidate.id">
									{{ memberLabel(candidate) }}{{ candidate.id === member.id ? ' (self)' : '' }}
								</SelectItem>
							</SelectContent>
						</Select>
					</template>
					<p v-else class="text-muted-foreground text-xs">Only an enterprise owner can change this handoff.</p>
				</ZhPanel>
				<p v-if="mailboxNotice" class="text-sm" data-testid="mailbox-owner-notice">{{ mailboxNotice }}</p>
				<MailboxPanel :key="String(isEnterpriseOwner)" :source="mailboxSource" />
			</section>
		</template>

		<Dialog :open="Boolean(pendingMailboxOwner)" @update:open="open => { if (!open) pendingMailboxOwnerId = null }">
			<DialogContent v-if="pendingMailboxOwner && mailboxOwnership">
				<DialogHeader>
					<DialogTitle>
						{{ restoringMailboxToSelf ? `Restore ${mailboxOwnership.address} to ${memberLabel(pendingMailboxOwner)}?` : `Hand off ${mailboxOwnership.address}?` }}
					</DialogTitle>
					<DialogDescription v-if="restoringMailboxToSelf">
						{{ memberLabel(pendingMailboxOwner) }} will resume receiving new mail and may send, reply and chase from this address.
					</DialogDescription>
					<DialogDescription v-else>
						{{ memberLabel(pendingMailboxOwner) }} will receive new mail and may send, reply and chase from this address.
						Choose {{ member ? memberLabel(member) : '' }} to restore self-ownership later.
					</DialogDescription>
				</DialogHeader>
				<div v-if="mailboxOwnership.existingMessages > 0" class="flex items-start gap-2">
					<Checkbox
						id="transfer-existing-mail"
						:model-value="transferExistingMail"
						:disabled="busy"
						@update:model-value="transferExistingMail = $event === true"
					/>
					<Label for="transfer-existing-mail">
						Move {{ mailboxOwnership.existingMessages }} existing
						{{ mailboxOwnership.existingMessages === 1 ? 'message' : 'messages' }} addressed to
						{{ mailboxOwnership.address }}
					</Label>
				</div>
				<DialogFooter>
					<Button type="button" variant="outline" :disabled="busy" @click="pendingMailboxOwnerId = null">Cancel</Button>
					<Button type="button" :disabled="busy" data-testid="confirm-mailbox-owner" @click="confirmMailboxOwner">
						Confirm handoff
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
		<Dialog :open="Boolean(emailPrompt)" @update:open="open => { if (!open) emailPrompt = null }">
			<DialogContent v-if="emailPrompt && member">
				<DialogHeader>
					<DialogTitle>Update email too?</DialogTitle>
					<DialogDescription>
						Renaming to <strong>{{ emailPrompt.name }}</strong>. Change the email from {{ member.email }} to
						<strong>{{ emailPrompt.email }}</strong>?
					</DialogDescription>
				</DialogHeader>
				<DialogFooter>
					<Button type="button" variant="outline" :disabled="busy" @click="rename({ name: emailPrompt.name })">
						Keep current email
					</Button>
					<Button type="button" :disabled="busy" @click="rename(emailPrompt)">Update email</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	</section>
</template>
